// Welcome Bonus — whether to advertise the bonus and whether a claim can succeed.
// Mirrors the backend claim gates reported by GET /payment/signup-bonus.

import { DAILY_BUDGET_USER_MESSAGE, type SignupBonus } from './signupBonusService';

/** Claim blocks meaning the bonus is switched off for this player (shop opted out / platform off). */
const NOT_OFFERED_BLOCK_CODES = new Set(['SHOP_NOT_OPTED_IN', 'FEATURE_DISABLED']);

/** Claim block the player clears by verifying a phone number or email. */
export const CONTACT_NOT_VERIFIED_CODE = 'CONTACT_NOT_VERIFIED';

/** Claim errors that another tap cannot fix (policy / already settled / wait until tomorrow). */
const BLOCKING_CLAIM_ERROR_CODES = new Set([
    'SHOP_NOT_OPTED_IN',
    'FEATURE_DISABLED',
    'SHOP_DEBT',
    'DAILY_BUDGET',
    'NOT_CLAIMABLE',
    'NOT_AVAILABLE',
    CONTACT_NOT_VERIFIED_CODE,
]);

export const CLAIM_BLOCKED_FALLBACK_MESSAGE =
    'Welcome bonus is not available right now. Please try again later.';

export const VERIFY_CONTACT_MESSAGE =
    'Verify your phone number to claim your welcome bonus.';

/**
 * False when an unclaimed bonus is switched off for this player, so no badge or modal should
 * advertise it. Claimed bonuses (active, in review, completed, expired) always stay visible.
 */
export function isSignupBonusOffered(bonus: SignupBonus | null | undefined): boolean {
    if (!bonus) return false;
    if (bonus.status !== 'inactive') return true;
    if (bonus.offered === false) return false;
    return !NOT_OFFERED_BLOCK_CODES.has(bonus.claimBlockCode ?? '');
}

export function isBlockingClaimErrorCode(code: string | null | undefined): boolean {
    return !!code && BLOCKING_CLAIM_ERROR_CODES.has(code);
}

/** Notice text when the status API reports a block without a message. */
export function claimBlockFallbackMessage(code: string): string {
    if (code === 'DAILY_BUDGET') return DAILY_BUDGET_USER_MESSAGE;
    if (code === CONTACT_NOT_VERIFIED_CODE) return VERIFY_CONTACT_MESSAGE;
    return CLAIM_BLOCKED_FALLBACK_MESSAGE;
}

/** True when an unclaimed bonus is waiting for the player to verify a phone number or email. */
export function needsContactVerification(
    bonus: SignupBonus | null | undefined,
    claimErrorCode?: string | null
): boolean {
    if (!bonus || bonus.status !== 'inactive') return false;
    return bonus.claimBlockCode === CONTACT_NOT_VERIFIED_CODE || claimErrorCode === CONTACT_NOT_VERIFIED_CODE;
}
